import {
  Navigate,
  useRoutes,
  BrowserRouter,
  Route,
  Switch,
} from "react-router-dom";
import React, { lazy, Suspense } from 'react';
import { CircularProgress } from "@mui/material";

// =======================
// OPTIONAL: Loading Component
// =======================
// Create a simple loading component to show while chunks are downloading
const Loader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </div>
);

// =======================
// LAZY IMPORTS
// =======================

// Layouts & Pages
const DashboardLayout = lazy(() => import("./layouts/dashboard"));
const Login = lazy(() => import("./pages/Login"));
const NotFound = lazy(() => import("./pages/Page404"));
const DashboardApp = lazy(() => import("./pages/DashboardApp"));
const LoginTeacher = lazy(() => import("./pages/LoginTeacher"));
const Student = lazy(() => import("./pages/User")); // File is User.js, Component is Student
const UserReport = lazy(() => import("./pages/UserReport"));
const Students = lazy(() => import("./pages/Students"));

// Dashboard Sections - Batches & Tests
const Batch = lazy(() => import("./sections/@dashboard/batch/Batch"));
const Test = lazy(() => import("./sections/@dashboard/testSeries/test"));
const AddTestSeries = lazy(() => import("./sections/@dashboard/testSeries/AddTestSeries"));
const AddBatch = lazy(() => import("./sections/@dashboard/batch/AddBatch"));
const BatchDetails = lazy(() => import("./sections/@dashboard/batch/BatchDetails"));
const LectureForm = lazy(() => import("./sections/@dashboard/batch/LectureForm"));
const LiveLecture = lazy(() => import("./sections/@dashboard/batch/LiveLecture"));
const StudentBatches = lazy(() => import("./sections/@dashboard/user/StudentBatches"));
const LectureResources = lazy(() => import("./sections/@dashboard/batch/LectureResources"));
const RecordedLectures = lazy(() => import("./sections/@dashboard/batch/RecordedLectures"));
const AssignTeacherToBatch = lazy(() => import("./sections/@dashboard/batch/AssignTeacherToBatch"));
const UpdateBatchDetails = lazy(() => import("./sections/@dashboard/batch/UpdateBatchDetails"));
const UpdateLectureDetails = lazy(() => import("./sections/@dashboard/batch/UpdateLectureDetails"));
const YoutubeLive = lazy(() => import("./sections/@dashboard/batch/YoutubeLive"));
const CenterLectureForm = lazy(() => import("./sections/@dashboard/batch/CenterLectureForm"));
const AddCenterLectureResources = lazy(() => import("./sections/@dashboard/batch/AddCenterLectureResources"));

// Manage Staff & Mentors
const Staff = lazy(() => import("./sections/@dashboard/manageStaff/Staff"));
const Mentors = lazy(() => import("./sections/@dashboard/mentors/Staff"));
const SubAdmin = lazy(() => import("./sections/@dashboard/manageStaff/SubAdmin"));
const Moderators = lazy(() => import("./sections/@dashboard/manageStaff/Moderators"));
const TeacherProfile = lazy(() => import("./sections/@dashboard/manageStaff/TeacherProfile"));
const UpdateStaff = lazy(() => import("./sections/@dashboard/manageStaff/UpdateStaff"));
const UpdateMentors = lazy(() => import("./sections/@dashboard/mentors/UpdateStaff"));

// General Settings
const Banner = lazy(() => import("./sections/@dashboard/generalSettiing/Banner"));
const ContactUs = lazy(() => import("./sections/@dashboard/generalSettiing/ContactUs"));
const Category = lazy(() => import("./sections/@dashboard/generalSettiing/Category"));
const Youtube = lazy(() => import("./sections/@dashboard/generalSettiing/Youtube"));
const HelpAndSupport = lazy(() => import("./sections/@dashboard/generalSettiing/HelpAndSupport"));
const Telegram = lazy(() => import("./sections/@dashboard/generalSettiing/Telegram"));
const Subject = lazy(() => import("./sections/@dashboard/generalSettiing/Subject"));
const LogoutUsers = lazy(() => import("./sections/@dashboard/generalSettiing/LogoutUsers"));
const SubCategory = lazy(() => import("./sections/@dashboard/generalSettiing/SubCategory"));

// Community
const Community = lazy(() => import("./sections/@dashboard/community/Community"));
const NewCommunity = lazy(() => import("./sections/@dashboard/community/NewCommunity"));
const CommunityDetails = lazy(() => import("./sections/@dashboard/community/CommunityDetails"));
const UpdateCommunity = lazy(() => import("./sections/@dashboard/community/UpdateCommunity"));
const MemberAccess = lazy(() => import("./sections/@dashboard/community/FollowRequests"));
const FollowRequests = lazy(() => import("./sections/@dashboard/community/FollowRequests"));

// Assignments & Resources
const Assignment = lazy(() => import("./sections/@dashboard/assignmennt/Assignment"));
const Testimonial = lazy(() => import("./sections/@dashboard/testimonial/Testimonial"));
const AIR = lazy(() => import("./sections/@dashboard/resources/AIR"));
const CustomNotification = lazy(() => import("./sections/@dashboard/resources/CustomNotification"));
const AddCustomNotification = lazy(() => import("./sections/@dashboard/resources/AddCustomNotification"));
const NewsClip = lazy(() => import("./sections/@dashboard/resources/NewsClip"));
const TeacherGenerated = lazy(() => import("./sections/@dashboard/resources/TeacherGenerated"));
const Pathyakram = lazy(() => import("./sections/@dashboard/resources/Pathyakram"));
const Notes = lazy(() => import("./sections/@dashboard/resources/Notes"));
const AddResources = lazy(() => import("./sections/@dashboard/resources/AddResources"));
const Resources = lazy(() => import("./sections/@dashboard/resources/Resources"));
const Recorded = lazy(() => import("./sections/@dashboard/resources/Recorded"));
const AddRecordedVideo = lazy(() => import("./sections/@dashboard/resources/AddRecordedVideo"));
const PreviousYearQuestionPapers = lazy(() => import("./sections/@dashboard/resources/PreviousYearQuestionPapers"));

// Live Stream
const LiveStream = lazy(() => import("./sections/@dashboard/livestream/LiveStream"));
const SrsLiveStream = lazy(() => import("./sections/@dashboard/livestream/SrsLiveStream"));

// Profile & Messaging
const Profile = lazy(() => import("./sections/@dashboard/profile/Profile"));
const Messaging = lazy(() => import("./sections/@dashboard/messaging/Messaging"));
const StudentList = lazy(() => import("./sections/@dashboard/user/StudentList"));
const StudentProfile = lazy(() => import("./sections/@dashboard/user/StudentProfile"));

// Test Series Details
const TestSeriesDetails = lazy(() => import("./sections/@dashboard/testSeries/TestSeriesDetails"));
const AddTest = lazy(() => import("./sections/@dashboard/testSeries/AddTest"));
const UpdateTestSeries = lazy(() => import("./sections/@dashboard/testSeries/UpdateTestSeries"));
const AttemptedTest = lazy(() => import("./sections/@dashboard/testSeries/AttemptedTest"));
const StudentTestSeries = lazy(() => import("./sections/@dashboard/testSeries/StudentTestSeries"));
const StudentTestSeriesList = lazy(() => import("./sections/@dashboard/testSeries/StudentTestSeriesList"));
const UpdateTest = lazy(() => import("./sections/@dashboard/testSeries/UpdateTest"));

// Quiz
const Quiz = lazy(() => import("./sections/@dashboard/quiz/Quiz"));
const AddQuizQuestion = lazy(() => import("./sections/@dashboard/quiz/AddQuizQuestion"));
const AddQuiz = lazy(() => import("./sections/@dashboard/quiz/AddQuiz"));
const EditQuestion = lazy(() => import("./sections/@dashboard/quiz/EditQuestion"));
const UpdateQuiz = lazy(() => import("./sections/@dashboard/quiz/UpdateQuiz"));
const IssuesTable = lazy(() => import("./sections/@dashboard/quiz/IssuesTable"));
const DoubtsTable = lazy(() => import("./sections/@dashboard/quiz/DoubtsTable"));
const QuizTable = lazy(() => import("./sections/@dashboard/quiz/QuizTable"));
const UpdateQuizQuestions = lazy(() => import("./sections/@dashboard/quiz/UpdateQuizQuestions"));
const QuizStudents = lazy(() => import("./sections/@dashboard/quiz/QuizStudents"));
const QuizSResults = lazy(() => import("./sections/@dashboard/quiz/QuizResults"));
const StudentResults = lazy(() => import("./sections/@dashboard/quiz/StudentResults"));
const LeaderBoard = lazy(() => import("./sections/@dashboard/quiz/LeaderBoard"));

// Coupons & Misc
const AllCoupon = lazy(() => import("./sections/@dashboard/coupon/AllCoupon"));
const AddCoupon = lazy(() => import("./sections/@dashboard/coupon/AddCoupon"));
const Oath2callback = lazy(() => import("./sections/@dashboard/Oath2callback"));

// Library
const Library = lazy(() => import("./sections/@dashboard/library/addQuestions/library"));
const AddQuestions = lazy(() => import("./sections/@dashboard/library/addQuestions/addquestion"));
const ViewLibrary = lazy(() => import("./sections/@dashboard/library/viewLibrary"));
const ViewQuestions = lazy(() => import("./sections/@dashboard/library/addQuestions/allquestions"));
const SelectQuestions = lazy(() => import("./sections/@dashboard/library/addQuestions/selectQuestions"));
// ----------------------------------------------------------------------

export default function Router() {
  const LoggedIn = localStorage.getItem("isLoggedIn");
  
  let d = { access: [] };
  try {
    const detailObject = localStorage.getItem('detailObject');
    if (detailObject) {
      d = JSON.parse(detailObject);
    }
  } catch (e) {
    d = { access: [] };
  }

  const role = localStorage.getItem('role')
  const routes = useRoutes([
    {
      path: "/dashboard",
      element: <DashboardLayout />,
      children: [
        { path: "app", element: <DashboardApp /> },
        // { path: "message", element: <Messaging /> },
        // { path: "logoutUsers", element: <LogoutUsers /> },
        // { path: "students", element: <Student /> },
        { path: "teacher/teacherProfile/:data", element: <TeacherProfile /> },
        { path: "students/studentsProfile/:data", element: <StudentProfile /> },
        { path: "profile", element: <Profile /> },
        // { path: "studentBatches", element: <StudentBatches /> },
        {
          path: "studentBatches/studentList/:active/:batchId",
          element: <StudentList />,
        },
        // { path: 'products', element: <Products /> },
        // { path: 'youtubeLive/:lectId/:lectTitle', element: <YoutubeLive /> },
        {
          path: "youtubeLive/:lectId/:lectTitle/:lectDesc/:ytUrl",
          element: <YoutubeLive />,
        },
        // { path: "batches", element: <Batch /> },
        // { path: "userReport", element: <UserReport /> },
        // { path: "purchaseReport", element: <Students /> },
        // { path: "batches", element: <Batch /> },
        {
          path: "batches/addTeacherToBatch/:batchId",
          element: <AssignTeacherToBatch />,
        },
        {
          path: "batches/updateBatchDetails/:batchId",
          element: <UpdateBatchDetails />,
        },
        { path: "helpAndSupport", element: <HelpAndSupport /> },
        // { path: "addBatch", element: <AddBatch /> },
        { path: "batchDetails/:Id", element: <BatchDetails /> },
        {
          path: "batchDetails/updateLectureDetails/:batchId/:lectId",
          element: <UpdateLectureDetails />,
        },
        {
          path: "batchDetails/addLecture/:Id/:batchName",
          element: <LectureForm />,
        },
        {
          path: "batchDetails/liveLecture/:Id/:batchName",
          element: <LiveLecture />,
        },
        // { path: "test", element: <Test /> },
        {
          path: "test/testSeriesDetails/updateTestDetails/:testSeriesId/:testId",
          element: <UpdateTest />,
        },
        // { path: "studentTestSeries", element: <StudentTestSeries /> },
        {
          path: "studentTestSeries/studentList/:active/:testSeriesId",
          element: <StudentTestSeriesList />,
        },
        {
          path: "test/updateTestSeries/:testSeriesId",
          element: <UpdateTestSeries />,
        },
        {
          path: "test/testSeriesDetails/:testSeriesId",
          element: <TestSeriesDetails />,
        },
        {
          path: "test/editquestion/:testSeriesId/:questionId/:data",
          element: <EditQuestion />,
        },
        {
          path: "test/editquestion/",
          element: <EditQuestion />,
        },
        {
          path: "test/testSeriesDetails/attemptedTest/:testSeriesId",
          element: <AttemptedTest />,
        },
        {
          path: "test/testSeriesDetails/addTest/:testSeriesId",
          element: <AddTest />,
        },
        { path: "recordedLectures", element: <RecordedLectures /> },
        // { path: "addTest", element: <AddTestSeries /> },
        // { path: "staff", element: <Staff /> },
        { path: "staff/updateStaff/:teacherId", element: <UpdateStaff /> },
        // {
        //   path: "staff/updatePermission/:teacherId",
        //   element: <UpdatePermission />,
        // },
        { path: "moderators", element: <Moderators /> },
        { path: "subAdmin", element: <SubAdmin /> },
        // { path: "banner", element: <Banner /> },
        // { path: "youtube", element: <Youtube /> },
        // { path: "telegram", element: <Telegram /> },
        // { path: "contactUs", element: <ContactUs /> },
        // { path: "category", element: <Category /> },
        // { path: "subCategory", element: <SubCategory /> },
        // { path: "subject", element: <Subject /> },
        { path: "addResources", element: <AddResources /> },
        // { path: "customNotification", element: <CustomNotification /> },
        { path: "addCustomNotification", element: <AddCustomNotification /> },
        { path: "resources", element: <Resources /> },
        {
          path: "lectureResources/:lectId/:batchId",
          element: <LectureResources />,
        },
        // { path: "assignment", element: <Assignment /> },
        // { path: "testimonial", element: <Testimonial /> },
        // { path: "pathyakram", element: <Pathyakram /> },
        // { path: "recorded", element: <Recorded /> },
        { path: "addRecorded/:lectId/:batchId", element: <AddRecordedVideo /> },
        // { path: "air", element: <AIR /> },
        // { path: "newsClip", element: <NewsClip /> },
        // { path: "notes", element: <Notes /> },
        // { path: "previousYearQuestionPapers", element: <PreviousYearQuestionPapers /> },
        // { path: "quiz", element: <Quiz /> },
        { path: "quiz/doubts/:quizName/:quizId", element: <DoubtsTable /> },
        { path: "quiz/issues/:quizName/:quizId", element: <IssuesTable /> },
        {
          path: "quiz/quizStudents/:quizName/:quizId",
          element: <QuizStudents />,
        },

        //User
        {
          path: "students",
          element:
            role !== "Admin" && !d?.access?.includes("All Users") ? (
              <Navigate to="/404" replace />
            ) : (
              <Student />
            ),
        },
        {
          path: "studentBatches",
          element:
            role !== "Admin" && !d?.access?.includes("Course Purchases") ? (
              <Navigate to="/404" replace />
            ) : (
              <StudentBatches />
            ),
        },
        {
          path: "studentTestSeries",
          element:
            role !== "Admin" &&
              !d?.access?.includes("Test Series Purchases") ? (
              <Navigate to="/404" replace />
            ) : (
              <StudentTestSeries />
            ),
        },
        {
          path: "purchaseReport",
          element:
            role !== "Admin" && !d?.access?.includes("Purchase Report") ? (
              <Navigate to="/404" replace />
            ) : (
              <Students />
            ),
        },
        {
          path: "userReport",
          element:
            role !== "Admin" && !d?.access?.includes("User Report") ? (
              <Navigate to="/404" replace />
            ) : (
              <UserReport />
            ),
        },
        //Batch
        {
          path: "batches",
          element:
            role !== "Admin" && !d?.access?.includes("All Batches") ? (
              <Navigate to="/404" replace />
            ) : (
              <Batch />
            ),
        },
        {
          path: "addBatch",
          element:
            role !== "Admin" && !d?.access?.includes("Add Batch") ? (
              <Navigate to="/404" replace />
            ) : (
              <AddBatch />
            ),
        },
        {
          path: "addCenterLecture",
          element:
            role !== "Admin" && !d?.access?.includes("Add Center Lecture") ? (
              <Navigate to="/404" replace />
            ) : (
              <CenterLectureForm />
            ),
        },
        {
          path: "addCenterResource",
          element:
            role !== "Admin" && !d?.access?.includes("Add Center Resource") ? (
              <Navigate to="/404" replace />
            ) : (
              <AddCenterLectureResources />
            ),
        },

        //Test Series
        {
          path: "test",
          element:
            role !== "Admin" && !d?.access?.includes("All Test Series") ? (
              <Navigate to="/404" replace />
            ) : (
              <Test />
            ),
        },
        {
          path: "addTest",
          element:
            role !== "Admin" && !d?.access?.includes("Add Test Series") ? (
              <Navigate to="/404" replace />
            ) : (
              <AddTestSeries />
            ),
        },

        //Quiz

        {
          path: "quiz",
          element:
            role !== "Admin" && !d?.access?.includes("All Quiz") ? (
              <Navigate to="/404" replace />
            ) : (
              <Quiz />
            ),
        },
        {
          path: "addQuiz",
          element:
            role !== "Admin" && !d?.access?.includes("Add Quiz") ? (
              <Navigate to="/404" replace />
            ) : (
              <AddQuiz />
            ),
        },

        //Coupon
        {
          path: "allCoupon",
          element:
            role !== "Admin" && !d?.access?.includes("All Coupon") ? (
              <Navigate to="/404" replace />
            ) : (
              <AllCoupon />
            ),
        },
        {
          path: "addCoupon",
          element:
            role !== "Admin" && !d?.access?.includes("Add Coupon") ? (
              <Navigate to="/404" replace />
            ) : (
              <AddCoupon />
            ),
        },

        //Resources
        {
          path: "recorded",
          element:
            role !== "Admin" && !d?.access?.includes("Recorded Video") ? (
              <Navigate to="/404" replace />
            ) : (
              <Recorded />
            ),
        },
        {
          path: "pathyakram",
          element:
            role !== "Admin" && !d?.access?.includes("Pathyakram") ? (
              <Navigate to="/404" replace />
            ) : (
              <Pathyakram />
            ),
        },
        {
          path: "notes",
          element:
            role !== "Admin" && !d?.access?.includes("Notes") ? (
              <Navigate to="/404" replace />
            ) : (
              <Notes />
            ),
        },

        {
          path: "previousYearQuestionPapers",
          element:
            role !== "Admin" &&
              !d?.access?.includes("Previous Year Question Papers") ? (
              <Navigate to="/404" replace />
            ) : (
              <PreviousYearQuestionPapers />
            ),
        },
        {
          path: "air",
          element:
            role !== "Admin" && !d?.access?.includes("AIR") ? (
              <Navigate to="/404" replace />
            ) : (
              <AIR />
            ),
        },
        ,
        {
          path: "newsClip",
          element:
            role !== "Admin" && !d?.access?.includes("News Clip") ? (
              <Navigate to="/404" replace />
            ) : (
              <NewsClip />
            ),
        },
        {
          path: "teacherGenerated",
          element:
            role !== "Admin" &&
              !d?.access?.includes("Teacher Generated Content Of Batch") ? (
              <Navigate to="/404" replace />
            ) : (
              <TeacherGenerated />
            ),
        },
        {
          path: "customNotification",
          element:
            role !== "Admin" && !d?.access?.includes("Alerts") ? (
              <Navigate to="/404" replace />
            ) : (
              <CustomNotification />
            ),
        },

        //Assignment
        {
          path: "assignment",
          element:
            role !== "Admin" && !d?.access?.includes("All Assignment") ? (
              <Navigate to="/404" replace />
            ) : (
              <Assignment />
            ),
        },

        // community

        {
          path: "community",
          element:
            role !== "Admin" && !d?.access?.includes("Community") ? (
              <Navigate to="/404" replace />
            ) : (
              <Community />
            ),
        },

        { path: "community/new", element: <NewCommunity /> }, // New route for New Community
        {
          path: "community/:id/details",
          element: <CommunityDetails />,
        }, // New route for New Community
        { path: "community/:id/edit", element: <UpdateCommunity /> },
        { path: "community/:id/follow", element: <FollowRequests /> },
        //Manage Staff
        {
          path: "staff",
          element:
            role !== "Admin" && !d?.access?.includes("All Staff") ? (
              <Navigate to="/404" replace />
            ) : (
              <Staff />
            ),
        },
        //Mentors
        {
          path: "mentors",
          element:
            role !== "Admin" && !d?.access?.includes("All mentors") ? (
              <Navigate to="/404" replace />
            ) : (
              <Mentors />
            ),
        },
        //Testimonial
        {
          path: "testimonial",
          element:
            role !== "Admin" && !d?.access?.includes("All Testimonial") ? (
              <Navigate to="/404" replace />
            ) : (
              <Testimonial />
            ),
        },

        //General Setting
        {
          path: "banner",
          element:
            role !== "Admin" && !d?.access?.includes("Banner") ? (
              <Navigate to="/404" replace />
            ) : (
              <Banner />
            ),
        },
        {
          path: "logoutUsers",
          element:
            role !== "Admin" && !d?.access?.includes("Logout Users") ? (
              <Navigate to="/404" replace />
            ) : (
              <LogoutUsers />
            ),
        },
        {
          path: "subject",
          element:
            role !== "Admin" && !d?.access?.includes("Subject") ? (
              <Navigate to="/404" replace />
            ) : (
              <Subject />
            ),
        },

        {
          path: "youtube",
          element:
            role !== "Admin" && !d?.access?.includes("Youtube") ? (
              <Navigate to="/404" replace />
            ) : (
              <Youtube />
            ),
        },
        {
          path: "telegram",
          element:
            role !== "Admin" && !d?.access?.includes("Telegram") ? (
              <Navigate to="/404" replace />
            ) : (
              <Telegram />
            ),
        },
        ,
        {
          path: "category",
          element:
            role !== "Admin" && !d?.access?.includes("Category") ? (
              <Navigate to="/404" replace />
            ) : (
              <Category />
            ),
        },
        {
          path: "subCategory",
          element:
            role !== "Admin" && !d?.access?.includes("Sub Category") ? (
              <Navigate to="/404" replace />
            ) : (
              <SubCategory />
            ),
        },
        {
          path: "contactUs",
          element:
            role !== "Admin" && !d?.access?.includes("Contact Us") ? (
              <Navigate to="/404" replace />
            ) : (
              <ContactUs />
            ),
        },
        {
          path: "message",
          element:
            role !== "Admin" && !d?.access?.includes("Help And Support") ? (
              <Navigate to="/404" replace />
            ) : (
              <Messaging />
            ),
        },

        {
          path: "quiz/leaderboard/:quizId/:quizName",
          element: <LeaderBoard />,
        },
        {
          path: "quiz/quizResults/:quizName/:quizId",
          element: <QuizSResults />,
        },
        {
          path: "quiz/editquestion/:quizId/:selectedIdOfQuestion/:quizTitle",
          element: <EditQuestion />,
        },
        // { path: "addQuiz", element: <AddQuiz /> },
        {
          path: "updateQuizQuestions/:questionId",
          element: <UpdateQuizQuestions />,
        },
        { path: "updateQuiz/:quizId", element: <UpdateQuiz /> },
        {
          path: "addQuizQuestion/:quizTitle/:quizId",
          element: <AddQuizQuestion />,
        },
        // { path: "quiz/:quizTitle/:quizId", element: <QuizQuestions /> },
        {
          path: "quiz/:quizTitle/:quizId/:studentId",
          element: <StudentResults />,
        },
        { path: "quiz/:quizTitle/:quizId", element: <QuizTable /> },
        // { path: "allCoupon", element: <AllCoupon /> },
        // { path: "addCoupon", element: <AddCoupon /> },
        // { path: "teacherGenerated", element: <TeacherGenerated /> },
        {
          path: "livestream/:channul/:lectId/:batchId/:desc",
          element: <SrsLiveStream />,
        },
        {
          path: "srslivestream/:channul/:lectId/:batchId/:desc",
          element: <SrsLiveStream />,
        },
        {
          path: "/dashboard/addQuestions",
          element: <AddQuestions />,
        },
        {
          path: "/dashboard/addQuestion",
          element: <Library />,
        },
        {
          path: "/dashboard/viewLibrary",
          element: <ViewLibrary />,
        },
        {
          path: "/dashboard/viewQuestion",
          element: <ViewQuestions />,
        },
        {
          path: "/dashboard/selectQuestion",
          element: <SelectQuestions />,
        },
      ],
    },
    { path: "oauth/callback/", element: <Oath2callback /> },

    //{ path: "oauth2callback/", element: <Oath2callback /> },
    {
      path: "login",
      element: <Login />,
    },
    // {
    //   path: "flutter",
    //   element: <FlutterLiveStream />,
    // },
    // {
    //   path: 'file',
    //   element: <APP />,
    // },
    {
      path: "loginTeacher",
      element: <LoginTeacher />,
    },


    {
      path: "/",
      element: LoggedIn ? <Navigate to="/dashboard/app" replace /> : <Login />,
    },
    {
      path: "/404",
      element: <NotFound />,
    },

    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ]);
  return (
    <Suspense fallback={<Loader />}>
      {routes}
    </Suspense>
  )

}
